import {Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {ClickEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {InterfaceEventsEnum} from '../../../services/tracking';
import {getHasRulesDryRun} from '../../../store/featureFlags/featureFlagsSelectors';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {buildExpressionName, Expression} from '../../editors/expressions/expressionModel';
import {RuleEditorSidebarTooltip} from '../../editors/ruleEditor/ruleEditorSidebarTooltip';
import {RuleRecipe} from '../library/recipes';
import {trackRuleEvent} from '../ruleHelpers';
import {EditorStateActions} from './hooks/useEditorState';
import {ExpressionPill} from './inputs/expressionPill';
import {RuleEditorAccountRuleTip} from './ruleEditorAccountRuleTip';
import {RuleEditorImpact} from './ruleEditorImpact';
import {
  StyledRuleEditorSidebarContainerDiv,
  StyledRuleEditorSidebarExpressionSection,
  StyledRuleEditorSidebarSection,
  StyledRuleEditorSidebarSectionTitle,
} from './ruleEditorSidebarSection';
import {RuleEditorTabsEnum} from './ruleEditorTabs';
import {RuleEditorTestDataSidebar} from './ruleEditorTestDataSidebar';
import {RuleEditorState} from './state/ruleEditorState';

/*
 * Types.
 */

interface RuleEditorSidebarProps {
  ruleEditorState: RuleEditorState;
  actions: EditorStateActions;
  namespace: string;
  recipe?: RuleRecipe;
  selectedTab: RuleEditorTabsEnum;
}

/*
 * Styles.
 */

const StyledExpressionInfoIcon = styled(Icon)`
  grid-area: icon;
`;

const StyledExpressionInfoButton = styled(RuleEditorSidebarTooltip)`
  grid-area: info;
`;

const StyledExpressionInfoTitle = styled.div`
  padding-left: 2px;
  grid-area: title;
`;

export const StyledRuleEditorAccordionWrap = styled.div`
  padding-left: 22px;
  margin-top: 7px;
`;

const StyledAccordionSectionTitleButton = styled(ChromelessButton)<{$isOpen: boolean}>`
  display: flex;
  padding: 5px 0;
  margin-left: -20px;
  margin-bottom: ${(p) => (p.$isOpen ? '10px' : '0')};
  color: ${(p) => (p.$isOpen ? p.theme.greys.shade90 : p.theme.greys.shade80)};
  font-weight: ${fontWeights.medium};

  &:hover {
    color: ${(p) => p.theme.greys.shade90};
  }
`;

const StyledRotateDiv = styled.div<{$isOpen: boolean}>`
  width: 20px;
  height: 20px;
  margin-right: 5px;
  transform: rotate(${(p) => (p.$isOpen ? '270deg' : '180deg')});
`;

const StyledMultipleButtonWrap = styled.div`
  display: flex;
  align-items: start;
  justify-content: flex-start;
  gap: 12px;
  padding-left: 30px;
`;

const StyledEmptyExpression = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  padding: 12px 12px 12px 30px;
`;

const DynamicVariablesSidebarSection = styled(StyledRuleEditorSidebarSection)`
  background-color: ${(p) => p.theme.greys.white};
  border: 1px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Component.
 */

// @TODO: Use ExpressionsSidebar.

export const RuleEditorSidebar = ({
  ruleEditorState,
  actions,
  namespace,
  recipe,
  selectedTab,
}: RuleEditorSidebarProps) => {
  const theme = useUserTheme();
  const {hasExpressions, isReadOnly} = useBaseEditorContext();
  const {expressions} = ruleEditorState;

  const onCreateClick = () => {
    actions.onSetPanelOpen(true);
    trackRuleEvent(
      InterfaceEventsEnum.SETTINGS_DYNAMIC_VARIABLE_CREATE_CLICK,
      ruleEditorState.initialRule,
      namespace,
      recipe?.id,
    );
  };

  const hasRulesDryRun = useAppSelector(getHasRulesDryRun);

  if (selectedTab && selectedTab !== RuleEditorTabsEnum.RULE_STRUCTURE) {
    return null;
  }

  return (
    <StyledRuleEditorSidebarContainerDiv>
      <RuleEditorImpact ruleEditorState={ruleEditorState} />
      {hasExpressions && expressions.length > 0 && (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <DynamicVariablesSidebarSection {...buildInteractionIdProps('rule-editor-expression-sidebar')}>
          <StyledRuleEditorSidebarSectionTitle>
            <StyledExpressionInfoIcon name="sparkles" color={theme.palette.teal.shade50} />
            <StyledExpressionInfoTitle>
              <Trans id="rule-editor-sidebar-variables-title">Dynamic Variables</Trans>
            </StyledExpressionInfoTitle>
            <StyledExpressionInfoButton
              rule={ruleEditorState.initialRule}
              namespace={namespace}
              recipe={recipe}
            />
          </StyledRuleEditorSidebarSectionTitle>
          <StyledRuleEditorSidebarExpressionSection>
            {renderExpressions(expressions, actions.onSelectExpression)}

            <StyledMultipleButtonWrap>
              <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onCreateClick}>
                <Trans id="rule-editor-sidebar-variables-create-new">Create new</Trans>
              </Button>
            </StyledMultipleButtonWrap>
          </StyledRuleEditorSidebarExpressionSection>
        </DynamicVariablesSidebarSection>
      )}
      {hasRulesDryRun && (
        <RuleEditorTestDataSidebar
          ruleEditorState={ruleEditorState}
          actions={actions}
          isReadOnly={isReadOnly}
        />
      )}
      <RuleEditorAccountRuleTip ruleEditorState={ruleEditorState} />
    </StyledRuleEditorSidebarContainerDiv>
  );
};

/*
 * Helpers.
 */

function renderExpressions(
  expressions: ReadonlyArray<Expression>,
  onSelect: EditorStateActions['onSelectExpression'],
) {
  if (expressions.length === 0) {
    return (
      <StyledEmptyExpression>
        <Trans id="rule-editor-sidebar-variables-none-created">None</Trans>
      </StyledEmptyExpression>
    );
  }

  return (
    <ul>
      {expressions.map((exp) => (
        <li key={exp.id}>
          <ChromelessButton onClick={() => onSelect(exp.id)}>
            <ExpressionPill label={buildExpressionName(exp)} type={exp.inferredType} dryRun={exp.dryRun} />
          </ChromelessButton>
        </li>
      ))}
    </ul>
  );
}

export function renderRuleEditorAccordionTitle(
  title: ReactNode,
  onClick: ClickEventHandler,
  isOpen: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledAccordionSectionTitleButton onClick={onClick} $isOpen={isOpen}>
          <StyledRotateDiv $isOpen={isOpen}>
            <Icon name="arrowBack" color={theme.greys.shade80} />
          </StyledRotateDiv>
          {title}
        </StyledAccordionSectionTitleButton>
      )}
    </UserThemeConsumer>
  );
}
